import React, { useState } from 'react';
import FolderPickerModal from './FolderPickerModal';

/**
 * Walks up the folder tree to build the full path array for a given parentId.
 */
const buildPath = (parentId, folderMap) => {
  const path = [];
  let current = folderMap.get(parentId);
  while (current) {
    path.unshift(current);
    current = current.parent ? folderMap.get(current.parent.toString()) : null;
  }
  return path;
};

const CreateFolderModal = ({ parentId, folders = [], folderMap = new Map(), users = [], userGroups = [], onSave, onClose }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedParentId, setSelectedParentId] = useState(parentId || '');
  const [allowedGroups, setAllowedGroups] = useState([]);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const path = buildPath(selectedParentId, folderMap);

  const handleSave = async () => {
    if (!title?.trim()) return;
    setError('');
    setSaving(true);
    try {
      await onSave({ title: title.trim(), description: description.trim(), parent: selectedParentId, allowedGroups, allowedUsers });
      onClose(); // only close on success
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Create New Folder</h3>
            <button className="secondary" type="button" onClick={onClose}>Close</button>
          </div>

          {/* Breadcrumb — shows where the new folder will live */}
          <div className="section">
            <div className="breadcrumb">
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Location:</span>
              <span className="breadcrumb-item">
                <span className="breadcrumb-separator">/</span>
                <span style={{ color: selectedParentId ? '#6b7280' : '#111827', fontWeight: selectedParentId ? 400 : 600 }}>
                  Root
                </span>
              </span>
              {path.map((item) => (
                <span key={item._id} className="breadcrumb-item">
                  <span className="breadcrumb-separator">/</span>
                  <span style={{ fontWeight: 600 }}>{item.title}</span>
                </span>
              ))}
              {title?.trim() && (
                <span className="breadcrumb-item">
                  <span className="breadcrumb-separator">/</span>
                  <span style={{ color: '#2563eb', fontWeight: 600 }}>{title.trim()}</span>
                </span>
              )}
            </div>
          </div>

          {/* Fields — same layout as edit modal */}
          <div className="section" style={{ display: 'grid', gap: '12px' }}>
            <label>Folder title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder="Enter folder name"
              autoFocus
            />

            <label>Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this folder for?"
              rows={3}
              style={{ minHeight: '80px', borderRadius: 'var(--radius-md)' }}
            />

            <label>Parent folder</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="secondary" onClick={() => setShowPicker(true)} style={{ flex: 1, textAlign: 'left', background: '#f8fafc', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                {selectedParentId ? folders.find(f => f._id === selectedParentId)?.title || 'Root folder' : 'Root folder'}
              </button>
              {selectedParentId && (
                <button type="button" className="icon-button danger" onClick={() => setSelectedParentId('')} title="Clear selection">
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ margin: '0 0 4px' }}>{error}</div>
          )}

          <div className="modal-actions">
            <button className="secondary" type="button" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" onClick={handleSave} disabled={!title?.trim() || saving}>
              {saving ? 'Creating…' : 'Create Folder'}
            </button>
          </div>
        </div>
      </div>

      {showPicker && (
        <FolderPickerModal
          title="Select Parent Folder"
          folders={folders}
          allowRoot={true}
          onSelect={(id) => { setSelectedParentId(id); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
};

export default CreateFolderModal;